import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { StaffShell } from '../../components/StaffShell';
import { PatientAvatar } from '../../components/PatientAvatar';
import { useUserStore } from '../../store/userStore';
import { getClinicianAvailability, addClinicianAvailability, removeClinicianAvailability, getClinicianAppointments, decideAppointment } from '../../lib/api';

const TIMES = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'];
const SLOT_STYLE = {
  open: { label: 'Open', cls: 'bg-emerald-500/15 text-emerald-300' },
  requested: { label: 'Requested', cls: 'bg-amber-500/15 text-amber-300' },
  booked: { label: 'Booked', cls: 'bg-[rgba(168,85,247,0.25)] text-white' },
};
const APPT_STYLE = {
  pending: 'bg-amber-500/15 text-amber-300',
  confirmed: 'bg-emerald-500/15 text-emerald-300',
  declined: 'bg-white/10 text-white/50',
  cancelled: 'bg-white/10 text-white/50',
};
const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

function AvailabilityEditor({ clinicianId, onAdded }) {
  const [dates, setDates] = useState([]);
  const [dateInput, setDateInput] = useState('');
  const [times, setTimes] = useState([]);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const addDate = () => {
    if (!dateInput || dates.includes(dateInput)) return;
    setDates((p) => [...p, dateInput].sort());
    setDateInput('');
  };
  const toggleTime = (t) => setTimes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t].sort()));

  const publish = async () => {
    if (!dates.length || !times.length) {
      toast.error('Pick at least one date and one time');
      return;
    }
    setSaving(true);
    try {
      const res = await addClinicianAvailability(clinicianId, { dates, times });
      toast.success(`${res.created} slot${res.created === 1 ? '' : 's'} published`);
      setDates([]); setTimes([]);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not publish availability');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-dark rounded-2xl p-5 mb-5" data-testid="availability-editor">
      <h2 className="font-display text-lg text-white mb-1 flex items-center gap-2"><i className="fa-solid fa-calendar-plus text-[var(--aura-purple-light)]" /> Set my availability</h2>
      <p className="text-white/50 text-sm mb-4">Choose the dates you're available, then the times. Patients will only see these slots when booking.</p>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs text-white/50 mb-1">Dates</label>
          <div className="flex gap-2 mb-2">
            <input data-testid="availability-date-input" type="date" min={today} value={dateInput} onChange={(e) => setDateInput(e.target.value)} className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white outline-none focus:border-[var(--aura-purple-light)]" />
            <button data-testid="availability-add-date" onClick={addDate} className="rounded-xl px-4 bg-white/10 text-white hover:bg-white/15 transition-base"><i className="fa-solid fa-plus" /></button>
          </div>
          <div className="flex flex-wrap gap-2 min-h-[2rem]">
            {dates.map((d) => (
              <span key={d} data-testid={`availability-date-${d}`} className="text-xs rounded-full px-3 py-1.5 bg-[rgba(168,85,247,0.25)] text-white flex items-center gap-2">
                {fmtDate(d)} <button onClick={() => setDates((p) => p.filter((x) => x !== d))} className="opacity-70 hover:opacity-100"><i className="fa-solid fa-xmark" /></button>
              </span>
            ))}
            {!dates.length && <span className="text-white/30 text-xs">No dates added yet</span>}
          </div>
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1">Times (applied to every date above)</label>
          <div className="flex flex-wrap gap-2">
            {TIMES.map((t) => (
              <button key={t} data-testid={`availability-time-${t}`} onClick={() => toggleTime(t)} className={`text-xs rounded-lg px-3 py-2 border transition-base ${times.includes(t) ? 'bg-[rgba(168,85,247,0.3)] border-[rgba(168,85,247,0.6)] text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}>{t}</button>
            ))}
          </div>
        </div>
      </div>
      <button data-testid="availability-publish" onClick={publish} disabled={saving} className="mt-4 aura-btn-gradient text-white font-semibold rounded-xl px-5 py-2.5 flex items-center gap-2 disabled:opacity-60">
        {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-bullhorn" />} Publish {dates.length * times.length || ''} slot{dates.length * times.length === 1 ? '' : 's'}
      </button>
    </div>
  );
}

function RequestCard({ appt, clinicianId, onDone }) {
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await decideAppointment(clinicianId, appt.id, decision);
      toast.success(decision === 'approve' ? 'Appointment confirmed' : 'Request declined — slot reopened');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update request');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0" data-testid={`request-${appt.id}`}>
      <PatientAvatar patient={{ name: appt.patient_name, profile_image: appt.patient_image }} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{appt.patient_name || 'Patient'}</div>
        <div className="text-xs text-white/50">{fmtDate(appt.date)} · {appt.time_slot} · {appt.appointment_type}</div>
      </div>
      {appt.status === 'pending' ? (
        <div className="flex gap-2 shrink-0">
          <button data-testid={`request-decline-${appt.id}`} onClick={() => decide('decline')} disabled={busy} className="text-xs rounded-lg px-3 py-2 border border-white/15 text-white/70 hover:bg-white/5 disabled:opacity-50">Decline</button>
          <button data-testid={`request-approve-${appt.id}`} onClick={() => decide('approve')} disabled={busy} className="text-xs rounded-lg px-3 py-2 bg-emerald-500 text-white font-semibold hover:bg-emerald-400 disabled:opacity-50"><i className="fa-solid fa-check mr-1" />Approve</button>
        </div>
      ) : (
        <span className={`text-xs font-semibold rounded-full px-3 py-1 capitalize ${APPT_STYLE[appt.status] || 'bg-white/10 text-white/60'}`}>{appt.status}</span>
      )}
    </div>
  );
}

export default function ClinicianSchedule() {
  const user = useUserStore((s) => s.user);
  const [slots, setSlots] = useState([]);
  const [appts, setAppts] = useState([]);

  const load = () => {
    getClinicianAvailability(user.id).then(setSlots).catch(() => {});
    getClinicianAppointments(user.id).then(setAppts).catch(() => {});
  };
  useEffect(() => { if (user?.id) load(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDate = useMemo(() => slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {}), [slots]);
  const pending = appts.filter((a) => a.status === 'pending');
  const upcoming = appts.filter((a) => a.status === 'confirmed');
  const history = appts.filter((a) => ['declined', 'cancelled', 'completed'].includes(a.status));

  const remove = async (id) => {
    try { await removeClinicianAvailability(user.id, id); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove slot'); }
  };

  return (
    <StaffShell title="Clinician" badge="Schedule">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-white mb-1">My schedule</h1>
        <p className="text-white/50">Publish when you're free, then approve the bookings patients request.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3">
          <AvailabilityEditor clinicianId={user.id} onAdded={load} />
          <div className="glass-dark rounded-2xl p-5" data-testid="availability-list">
            <h2 className="font-display text-lg text-white mb-3 flex items-center gap-2"><i className="fa-solid fa-calendar-days text-[var(--aura-purple-light)]" /> Upcoming availability</h2>
            {Object.keys(byDate).length === 0 ? <p className="text-white/40 text-sm">No slots published yet.</p> : null}
            {Object.entries(byDate).map(([d, list]) => (
              <div key={d} className="mb-3">
                <div className="text-xs uppercase tracking-wide text-white/40 mb-1.5">{fmtDate(d)}</div>
                <div className="flex flex-wrap gap-2">
                  {list.map((s) => {
                    const st = SLOT_STYLE[s.status] || SLOT_STYLE.open;
                    return (
                      <span key={s.id} data-testid={`slot-${s.id}`} className={`text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-2 ${st.cls}`}>
                        {s.time} <span className="opacity-60">· {st.label}</span>
                        {s.status === 'open' ? <button data-testid={`slot-remove-${s.id}`} onClick={() => remove(s.id)} title="Remove slot" className="opacity-60 hover:opacity-100"><i className="fa-solid fa-xmark" /></button> : null}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="glass-dark rounded-2xl p-5 mb-5" data-testid="booking-requests">
            <h2 className="font-display text-lg text-white mb-1 flex items-center gap-2"><i className="fa-solid fa-bell text-amber-300" /> Booking requests {pending.length ? <span className="text-xs rounded-full px-2 py-0.5 bg-amber-500/20 text-amber-300">{pending.length}</span> : null}</h2>
            <p className="text-white/50 text-xs mb-2">Patients can't attend until you approve.</p>
            {pending.length === 0 ? <p className="text-white/40 text-sm">No requests waiting.</p> : pending.map((a) => <RequestCard key={a.id} appt={a} clinicianId={user.id} onDone={load} />)}
          </div>
          <div className="glass-dark rounded-2xl p-5 mb-5" data-testid="confirmed-appointments">
            <h2 className="font-display text-lg text-white mb-2 flex items-center gap-2"><i className="fa-solid fa-calendar-check text-emerald-300" /> Confirmed</h2>
            {upcoming.length === 0 ? <p className="text-white/40 text-sm">Nothing confirmed yet.</p> : upcoming.map((a) => <RequestCard key={a.id} appt={a} clinicianId={user.id} onDone={load} />)}
          </div>
          {history.length > 0 && (
            <div className="glass-dark rounded-2xl p-5" data-testid="appointment-history">
              <h2 className="font-display text-lg text-white mb-2 flex items-center gap-2"><i className="fa-solid fa-clock-rotate-left text-white/50" /> History</h2>
              {history.map((a) => <RequestCard key={a.id} appt={a} clinicianId={user.id} onDone={load} />)}
            </div>
          )}
        </div>
      </div>
    </StaffShell>
  );
}
